import { useState } from 'react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import FormLabel from '@mui/material/FormLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Typography from '@mui/material/Typography'

// How many options a question needs before it counts as answered.
export const neededCount = (question) => (question.type === 'multi' ? question.selectCount || 1 : 1)
export const isAnswered = (question, answers) => (answers?.[question.id]?.length ?? 0) >= neededCount(question)

const optionSx = {
  alignItems: 'flex-start',
  mx: 0,
  my: 0.5,
  px: 1,
  py: 0.5,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  '& .MuiFormControlLabel-label': { pt: '9px', pb: '9px' },
}

// One assessment question: a fieldset whose legend is the stem. Never shows
// whether an option is right - the server does not send that.
const QuestionCard = ({ question, number, total, selected = [], onChange }) => {
  const [limitHint, setLimitHint] = useState('')
  const need = neededCount(question)
  const multi = question.type === 'multi'

  const toggle = (optionId) => {
    if (selected.includes(optionId)) {
      setLimitHint('')
      onChange(selected.filter((id) => id !== optionId))
      return
    }
    if (selected.length >= need) {
      setLimitHint(`You can select ${need} answers. Untick one to choose a different answer.`)
      return
    }
    setLimitHint('')
    onChange([...selected, optionId])
  }

  return (
    <FormControl component="fieldset" fullWidth sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
      <FormLabel
        component="legend"
        sx={{ color: 'text.primary', '&.Mui-focused': { color: 'text.primary' }, p: 0, mb: 1.5, whiteSpace: 'normal' }}
      >
        <Typography component="span" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
          Question {number} of {total}
        </Typography>
        <Typography component="span" sx={{ display: 'block', fontWeight: 700, whiteSpace: 'pre-line' }}>
          {question.stem}
        </Typography>
        {multi && (
          <Typography component="span" sx={{ display: 'block', mt: 1 }}>
            Select {need}
          </Typography>
        )}
      </FormLabel>

      {multi ? (
        <FormGroup>
          {question.options.map((option) => (
            <FormControlLabel
              key={option.id}
              sx={optionSx}
              control={<Checkbox checked={selected.includes(option.id)} onChange={() => toggle(option.id)} />}
              label={option.text}
            />
          ))}
        </FormGroup>
      ) : (
        <RadioGroup
          name={`question-${question.id}`}
          value={selected[0] ?? ''}
          onChange={(event) => onChange([event.target.value])}
        >
          {question.options.map((option) => (
            <FormControlLabel key={option.id} value={option.id} sx={optionSx} control={<Radio />} label={option.text} />
          ))}
        </RadioGroup>
      )}

      <Box aria-live="polite" sx={{ minHeight: '1.6em', mt: 0.5 }}>
        {multi && (
          <Typography>
            {limitHint || `${selected.length} of ${need} selected`}
          </Typography>
        )}
      </Box>
    </FormControl>
  )
}

export default QuestionCard
